import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';

import { ReturnIcon } from 'shared/components/ihcl/icon';
import { Input } from 'shared/components/ihcl/input';
import { Center } from 'shared/components/ihcl/positioning';
import { styled, useStyletron, withStyle } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { ButtonSelect } from 'shared/components/ihcl/v2/button-select';
import {
  educationLevelsExcludingStudent,
  educationLevelsForTechs,
  educationLevels as educationLevelsOrig,
} from 'shared/constants/selectOptions';

const CustomCertInputWrapper = styled('div', ({ $theme }) => ({
  paddingTop: $theme.sizing.scale600,
  color: $theme.colors.gray,
}));

const IsolatedSteps = styled('div', () => ({
  minHeight: '150vh',
}));

const FirstIsolatedStep = styled('div', () => ({
  width: '100%',
}));

const IsolatedStep = withStyle(FirstIsolatedStep, () => ({
  paddingTop: '40px',
}));

const yesNoOptions = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' },
];

const popularCertificationOptions = [
  'ACLS',
  'BLS',
  'CPR',
  'NRP',
  'PALS',
  'RN',
  'ONS/ONCC',
].map((cert) => ({ label: cert, value: cert }));

type OnboardingCredentialsProps = {
  nextAction?: string;
  previousAction?: string;
  stepNumber: number;
  useEducationOptionsExcludingStudent?: boolean;
};
const OnboardingCredentials = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
  useEducationOptionsExcludingStudent = false,
}: OnboardingCredentialsProps) => {
  // FIXME: Context should work without casting to any
  const {
    onboardingData,
    profileType = 'nurse',
    setOnboardingData,
  }: any = useContext(OnboardingContext);
  const {
    acute_care_experience: acuteCareExperience,
    certifications = [],
    clinical_care_experience: clinicalCareExperience,
    education_level: educationLevelValue,
  } = onboardingData;
  const [, theme] = useStyletron();
  const [isAddingCertification, setIsAddingCertification] = useState(false);
  const [customCertificationText, setCustomCertificationText] = useState('');
  const [customCertificationOptions, setCustomCertificationOptions] = useState(
    []
  );
  const educationLevelRef = useRef(null);
  const recentHospitalExpRef = useRef(null);
  const clinicalCareExpRef = useRef(null);
  const certificationsRef = useRef(null);
  let showAcuteCareExperienceQuestions = true;
  let educationLevels = educationLevelsOrig;
  if (useEducationOptionsExcludingStudent) {
    educationLevels = educationLevelsExcludingStudent;
  }
  if (profileType === 'tech') {
    educationLevels = educationLevelsForTechs;
    showAcuteCareExperienceQuestions = false;
  }
  const certificationOptions = popularCertificationOptions.concat(
    customCertificationOptions
  );
  const isNextDisabled =
    !educationLevelValue ||
    (showAcuteCareExperienceQuestions && !acuteCareExperience) ||
    (showAcuteCareExperienceQuestions &&
      acuteCareExperience === 'no' &&
      !clinicalCareExperience) ||
    !certifications ||
    certifications.length === 0;
  const addCustomCertification = () => {
    if (
      !customCertificationOptions.some(
        (certificationOption) =>
          certificationOption.label === customCertificationText
      )
    ) {
      setCustomCertificationOptions([
        ...customCertificationOptions,
        {
          label: customCertificationText,
          value: customCertificationText,
        },
      ]);
    }
    if (!certifications.includes(customCertificationText)) {
      setOnboardingData({
        ...onboardingData,
        certifications: [...certifications, customCertificationText],
      });
    }
    setCustomCertificationText('');
    setIsAddingCertification(false);
  };

  useEffect(() => {
    if (
      educationLevelValue &&
      !(acuteCareExperience || clinicalCareExperience) &&
      showAcuteCareExperienceQuestions
    ) {
      recentHospitalExpRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (
      acuteCareExperience &&
      !(certifications.length || clinicalCareExperience)
    ) {
      if (acuteCareExperience === 'no') {
        clinicalCareExpRef.current.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      certificationsRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (
      (acuteCareExperience === 'yes' || clinicalCareExperience) &&
      !certifications.length
    ) {
      certificationsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    educationLevelValue,
    acuteCareExperience,
    clinicalCareExperience,
    certifications.length,
  ]);

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={isNextDisabled}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={[
        'acute_care_experience',
        'certifications',
        'clinical_care_experience',
        'education_level',
      ]}
      stepName="Credentials"
    >
      <Center>
        <OnboardingTitle>Show off your credentials!</OnboardingTitle>
        <IsolatedSteps>
          <FirstIsolatedStep ref={educationLevelRef}>
            <OnboardingSubtitle>
              What&apos;s your highest education level?
            </OnboardingSubtitle>
            <ButtonSelect
              className="flex-col items-center"
              hideUnselected
              options={educationLevels}
              selectionMode="single"
              values={educationLevelValue ? [educationLevelValue] : []}
              onValuesChange={([value]) => {
                setOnboardingData({
                  ...onboardingData,
                  education_level: value ?? null,
                });
              }}
            />
          </FirstIsolatedStep>
          {showAcuteCareExperienceQuestions && educationLevelValue && (
            <IsolatedStep ref={recentHospitalExpRef}>
              <OnboardingSubtitle>
                Have you worked in a hospital within the last&nbsp;year?
              </OnboardingSubtitle>
              <ButtonSelect
                className="justify-center"
                options={yesNoOptions}
                selectionMode="single"
                values={acuteCareExperience ? [acuteCareExperience] : []}
                onValuesChange={([value]) => {
                  setOnboardingData({
                    ...onboardingData,
                    acute_care_experience: value,
                  });
                }}
              />
            </IsolatedStep>
          )}
          {showAcuteCareExperienceQuestions && acuteCareExperience === 'no' && (
            <IsolatedStep ref={clinicalCareExpRef}>
              <OnboardingSubtitle>
                Do you have 1+ years of experience in a clinical care setting?
              </OnboardingSubtitle>
              <ButtonSelect
                className="justify-center"
                options={yesNoOptions}
                selectionMode="single"
                values={clinicalCareExperience ? [clinicalCareExperience] : []}
                onValuesChange={([value]) => {
                  setOnboardingData({
                    ...onboardingData,
                    clinical_care_experience: value,
                  });
                }}
              />
            </IsolatedStep>
          )}
          {(acuteCareExperience === 'yes' ||
            clinicalCareExperience ||
            (!showAcuteCareExperienceQuestions && educationLevelValue)) && (
            <IsolatedStep ref={certificationsRef}>
              <OnboardingSubtitle>
                What certifications do you have?
              </OnboardingSubtitle>
              <OnboardingSubtitle>
                Please select all that apply
              </OnboardingSubtitle>
              <ButtonSelect
                className="justify-center"
                options={certificationOptions}
                size="compact"
                values={certifications || []}
                onValuesChange={(newCertifications) => {
                  setOnboardingData({
                    ...onboardingData,
                    certifications: newCertifications,
                  });
                }}
              />
              {!isAddingCertification && (
                <Button
                  variant="minimal"
                  onClick={() => {
                    setIsAddingCertification(true);
                  }}
                >
                  + Add another certification
                </Button>
              )}
              {isAddingCertification && (
                <CustomCertInputWrapper>
                  <Input
                    autoFocus
                    enterKeyHint="done"
                    label="Enter your certification"
                    value={customCertificationText}
                    endEnhancer={
                      <ReturnIcon
                        onClick={addCustomCertification}
                        color={theme.colors.gray}
                      />
                    }
                    onKeyDown={(evt) => {
                      if (evt.keyCode === 13) {
                        if (!customCertificationText) {
                          return;
                        }
                        addCustomCertification();
                      }
                    }}
                    onChange={(evt) =>
                      setCustomCertificationText(evt.currentTarget.value)
                    }
                  />
                </CustomCertInputWrapper>
              )}
            </IsolatedStep>
          )}
        </IsolatedSteps>
      </Center>
    </Onboarding>
  );
};

export default OnboardingCredentials;
